import {Eye} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {useServices} from '@/services/context'
import {useQuery} from '@tanstack/react-query'
import type {AllowedValue, AttributeDefinition} from '@/types/attribute-definition'
import {ValueSourceType} from '@/types/attribute-definition'

interface ValueSourcePreviewProps {
    definition: AttributeDefinition
    testId?: string
}

export function ValueSourcePreview({definition, testId}: ValueSourcePreviewProps) {
    const services = useServices()
    const vs = definition.validationRules?.valueSource
    const hasServerValues = definition.resolvedAllowedValues && definition.resolvedAllowedValues.length > 0

    const isComplete = !!vs && (
        (vs.type === ValueSourceType.LIST && vs.values && vs.values.length > 0)
        || (vs.type === ValueSourceType.ENUM && !!vs.typeName?.trim())
        || (vs.type === ValueSourceType.SQL && !!vs.sql?.trim())
        || (vs.type === ValueSourceType.SQL_LABELED && !!vs.sql?.trim())
        || (vs.type === ValueSourceType.CUSTOM_LIST && !!vs.listName?.trim())
    )

    const {data: draftValues} = useQuery({
        queryKey: ['resolve-values-draft', JSON.stringify(definition.validationRules)],
        queryFn: () => services.attributeDefinitions.resolveValuesDraft(definition.validationRules) as Promise<AllowedValue[]>,
        enabled: isComplete && !hasServerValues,
    })

    const values = hasServerValues ? definition.resolvedAllowedValues! : (draftValues ?? [])

    if (values.length === 0) return null

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" data-testid={testId}>
                    <Eye className="h-4 w-4"/>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2">
                <div className="flex flex-wrap gap-1">
                    {values.map(v => (
                        <Badge key={v.code} variant="outline" className="text-xs">{v.label}</Badge>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    )
}
